import { useEffect, useState } from 'react';
import { Button, Col, DatePicker, DatePickerProps, Form, Input, Row, Select } from 'antd';
import { FormProps } from 'antd/es/form/Form';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

// services
import NotificationService from 'core/services/notification';
import { onGetAllCountries } from 'modules/countries/services/countries';
import { CreateUserPayload, onCreateUser } from 'modules/users/services/users';
import { onGetAllLanguages } from 'modules/languages/services/languages';

// components
import BackButton from 'components/backButton/backButton';
import Icon from 'utils/icon';
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import { useTranslation } from 'react-i18next';

type FieldType = {
	name_ar: string;
	name_en: string;
	national_id: number;
	date_of_birth: string;
	phone_country_code: string;
	phone_number: string;
	password: string;
	password_confirmation: string;
	address: string;
	locale: string;
	country_id: number;
	email?: string;
};

function CreateUserPage() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [isCountriesLoading, setIsCountriesLoading] = useState(true);
	const [isLanguagesLoading, setIsLanguagesLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [countries, setCountries] = useState<{ label: string; value: number }[]>([]);
	const [languages, setLanguages] = useState<{ label: string; value: string }[]>([]);
	const [createForm] = Form.useForm();

	const validatePassword = (_: any, value: string) => {
		if (!value || createForm.getFieldValue('password') === value) {
			return Promise.resolve();
		}
		return Promise.reject(new Error(t('USERS.PASSWORDS_NOT_MATCH')));
	};

	useEffect(() => {
		getCountries();
		getLanguages();
	}, []);

	const getCountries = async () => {
		try {
			const response = await onGetAllCountries();
			setCountries(
				response.data.data.countries.map((el: any) => {
					return { label: el.name, value: el.id };
				}),
			);
			setIsCountriesLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const getLanguages = async () => {
		try {
			const response = await onGetAllLanguages();
			setLanguages(
				response.data.data.languages.map((el) => {
					return { label: el.name, value: el.code };
				}),
			);
			setIsLanguagesLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const handleDateChange: DatePickerProps['onChange'] = (date) => {
		createForm.setFieldsValue({ date_of_birth: date });
	};

	const handlePhoneChange = ({ dialCode, number, isValid }: PhoneInputReturn) => {
		if (isValid) {
			createForm.setFieldsValue({
				phone_country_code: dialCode,
				phone_number: number,
			});
		} else {
			createForm.setFields([
				{
					name: 'phone_number',
					errors: [t('COMMON.INVALID_PHONE')],
				},
			]);
		}
	};

	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const createUserPayload: CreateUserPayload = {
				name: {
					ar: values.name_ar,
					en: values.name_en,
				},
				national_id: `${values.national_id}`,
				date_of_birth: dayjs(values.date_of_birth).format('YYYY-MM-DD hh:mm:ss'),
				phone_country_code: values.phone_country_code.replace('+', ''),
				phone_number: values.phone_number,
				password: values.password,
				password_confirmation: values.password_confirmation,
				address: values.address,
				profile_status: 'Active',
				locale: values.locale,
				country_id: values.country_id,
				email: values.email,
				
			};

			await onCreateUser(createUserPayload);
			NotificationService.success(t('COMMON.SUCCESS'), t('USERS.CLIENT_CREATED'));
			navigate('/users');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.OPS'), t('USERS.CLIENT_NOT_CREATED'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('USERS.ADD_CLIENT')}</h3>
			</div>

			<div className="sectionContainer">
				<h5 style={{ marginBottom: 20 }}>{t('USERS.CLIENT_DETAILS')}</h5>

				<Form name="createForm" form={createForm} onFinish={onSubmit}>
					<Row gutter={[20, 20]}>
						{/* ar name input */}
						<Col span={24} md={12}>
							<h6>{t('USERS.NAME_ARABIC')}</h6>
							<Form.Item<FieldType>
								name="name_ar"
								rules={[
									{ required: true, message: t('USERS.AR_NAME_REQUIRED') },
									{ min: 12, message: t('USERS.MIN_12') },
									{ max: 60, message: t('USERS.MAX_60') },
								]}>
								<Input size="large" placeholder={t('USERS.ENTER_NAME_ARABIC')} />
							</Form.Item>
						</Col>

						{/* en name input */}
						<Col span={24} md={12}>
							<h6>{t('USERS.NAME_ENGLISH')}</h6>
							<Form.Item<FieldType>
								name="name_en"
								rules={[
									{ required: true, message: t('USERS.EN_NAME_REQUIRED') },
									{ min: 12, message: t('USERS.MIN_12') },
									{ max: 60, message: t('USERS.MAX_60') },
								]}>
								<Input size="large" placeholder={t('USERS.ENTER_NAME_ENGLISH')} />
							</Form.Item>
						</Col>

						{/* national id input */}
						<Col span={24} md={12}>
							<h6>{t('USERS.NATIONAL_ID')}</h6>
							<Form.Item<FieldType>
								name="national_id"
								rules={[
									{ required: true, message: t('USERS.NATIONAL_ID_REQUIRED') },
									{ len: 10, message: t('USERS.NATIONAL_ID_10') },
								]}>
								<Input size="large" placeholder={t('USERS.ENTER_NATIONAL_ID')} />
							</Form.Item>
						</Col>

						{/* email input */}
						<Col span={24} md={12}>
							<h6>{t('USERS.EMAIL')}</h6>
							<Form.Item<FieldType> name="email" rules={[{ type: 'email', message: t('USERS.EMAIL_NOT_VALID') }]}>
								<Input size="large" placeholder={t('USERS.ENTER_EMAIL')} />
							</Form.Item>
						</Col>

						{/* phone input */}
						<Col span={24} md={12}>
							<h6>{t('COMMON.PHONE_NUMBER')}</h6>
							<Form.Item<FieldType> name="phone_country_code" hidden>
								<Input hidden />
							</Form.Item>
							<Form.Item<FieldType> name="phone_number">
								<PhoneInputComponent onChange={handlePhoneChange} />
							</Form.Item>
						</Col>

						{/* address input */}
						<Col span={24} md={12}>
							<h6>{t('USERS.ADDRESS')}</h6>
							<Form.Item<FieldType> name="address" rules={[{ required: true, message: t('USERS.CLIENT_ADDRESS_REQUIRED') }]}>
								<Input size="large" placeholder={t('USERS.ENTER_ADDRESS')} />
							</Form.Item>
						</Col>

						{/* password input */}
						<Col span={24} md={12}>
							<h6>{t('USERS.PASSWORD')}</h6>
							<Form.Item<FieldType>
								name="password"
								rules={[
									{ required: true, message: t('USERS.CLIENT_PASSWORD_REQUIRED') },
									{
										min: 8,
										message: t('USERS.PASSWORD_8_CHARS'),
									},
								]}>
								<Input.Password size="large" placeholder={t('USERS.ENTER_PASSWORD')} />
							</Form.Item>
						</Col>

						{/* confirm password input */}
						<Col span={24} md={12}>
							<h6>{t('USERS.CONFIRM_PASSWORD')}</h6>
							<Form.Item<FieldType>
								name="password_confirmation"
								rules={[{ required: true, message: t('USERS.CONFIRM_PASSWORD_REQUIRED') }, { validator: validatePassword }]}>
								<Input.Password size="large" placeholder={t('USERS.ENTER_CONFIRM_PASSWORD')} />
							</Form.Item>
						</Col>

						{/* date of birth input */}
						<Col span={24} md={12}>
							<h6>{t('USERS.DATE_OF_BIRTH')}</h6>
							<Form.Item<FieldType> name="date_of_birth" rules={[{ required: true, message: t('USERS.DATE_BIRTH_REQUIRED') }]}>
								<DatePicker
									size="large"
									inputReadOnly
									style={{ width: '100%' }}
									maxDate={dayjs('30-10-2006', 'DD-MM-YYYY')}
									suffixIcon={<Icon name="calendar" />}
									onChange={handleDateChange}
									placeholder={t('USERS.ENTER_DATE_BIRTH')}
									allowClear={false}
								/>
							</Form.Item>
						</Col>

						<Col span={24} md={12}>
							<h6>{t('USERS.LOCALE')}</h6>
							<Form.Item<FieldType> name="locale" rules={[{ required: true, message: t('USERS.LOCAL_REQUIRED') }]}>
								<Select style={{ width: '100%' }} size="large" options={languages} loading={isLanguagesLoading} />
							</Form.Item>
						</Col>

						<Col span={24} md={12}>
							<h6>{t('USERS.COUNTRY')}</h6>
							<Form.Item<FieldType> name="country_id" rules={[{ required: true, message: t('USERS.COUNTRY_REQUIRED') }]}>
								<Select style={{ width: '100%' }} size="large" options={countries} loading={isCountriesLoading} />
							</Form.Item>
						</Col>
					</Row>
				</Form>

				<Form.Item className="mw-100">
					<Button
						size="middle"
						type="primary"
						htmlType="submit"
						form="createForm"
						loading={isSubmitting}
						style={{ marginTop: 20, marginInlineStart: 'auto', display: 'block' }}>
						{t('COMMON.ADD')}
					</Button>
				</Form.Item>
			</div>
		</>
	);
}

export default CreateUserPage;
